import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { useConfig } from '@packages/utilities';
import { getOffersJSON } from './getOffersJSON';

const SchemaOrgFragment = graphql(/* GraphQL */ `
  fragment SchemaOrgFragment on Product {
    name
    ean
    media {
      type
      url
    }
    offers {
      currency
      price
      availability
      sku
    }
    price {
      currency
      value
    }
    brand {
      name
      image
    }
    slug
    akl
    sku
    orderNumber
    availability {
      ...getSchemaAvailabilityFragment
    }
    sellingPoints
    longDescription
    ratingForSchema: seoRating(locale: $locale) {
      product {
        avgRating
        reviewCount
      }
    }
  }
`);

export const SchemaOrg = ({
  productData,
}: {
  productData: FragmentType<typeof SchemaOrgFragment>;
}): string => {
  const {
    offers,
    name,
    media,
    akl,
    brand,
    price,
    ratingForSchema,
    ean,
    longDescription,
    sellingPoints,
    slug,
    availability,
    sku,
  } = getFragmentData(SchemaOrgFragment, productData);
  const { company, host } = useConfig();

  const hostName =
    typeof window !== 'undefined'
      ? `${window?.location?.protocol}//${window?.location?.hostname}`
      : `https://www.${host.domain}`;

  const url = `${hostName}/p/${slug}/${akl}`;

  return `{
      "@context": "https://schema.org/",
      "@type": "Product",
      "url": "${url}",
      "gtin13": "${ean}",
      "name": ${JSON.stringify(name)},
      "image": ${JSON.stringify(media.map((image) => image.url))},
      "description": ${JSON.stringify(longDescription || sellingPoints.join('. '))},
      "sku": "${akl}",
      "brand": {
        "@type": "Brand",
        "name": ${JSON.stringify(brand?.name || '')},
        "logo": "${brand?.image}"
      },
      ${
        ratingForSchema?.product && ratingForSchema.product.reviewCount > 0
          ? `"aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "${ratingForSchema.product.avgRating}",
            "reviewCount": "${ratingForSchema.product.reviewCount}",
            "bestRating": 5,
            "worstRating": 1
          },`
          : ''
      }
      "offers": ${JSON.stringify(
        getOffersJSON({
          currentProduct: {
            price,
            akl,
            availability,
            sku,
          },
          companyName: company.name,
          offers,
          url,
        }),
      )}
    }
  `;
};
